import { EnergyLabel } from "@omni/components/components/Product/energyLabel/energyLabel";
import { KeyFeatures } from "@omni/components/components/Product/Feature/KeyFeatures/keyFeatures.container";
import { useProductPageContext } from "@omni/components/components/ProductPage/context/productPage.context";
import { ProductPage } from "@omni/components/components/ProductPage/productPage";
import { useFeatureToggle } from "@omni/components/context/featureToggles.context";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { Divider } from "@omni/ui/components/Divider/divider.component";
import { cn } from "@omni/utils/styles";

import { ECommerceContainer } from "@/components/Product/ECommerce/eCommerce.container";
import { InfoCards } from "@/components/Product/ProductPage/components/infoCards/infoCards.container";
import { Usps } from "@/components/Product/ProductPage/components/usps/usps.container";

export const ProductPageComponent = () => {
    const {
        product,
        infoLink,
        tooltips,
        features,
        energyLabel,
        cashbackPagePath,
        contextAwareXfPath,
        paintConfiguratorPath
    } = useProductPageContext();

    const isSticky = useFeatureToggle(
        FeatureToggleKey.FEATURE_STICKY_ECOMMERCE
    );

    const isReviewsEnabled = useFeatureToggle(FeatureToggleKey.FEATURE_REVIEWS);

    return (
        <>
            <ProductPage.Title>
                <ProductPage.Title.RatingSummary />
            </ProductPage.Title>
            <div
                className={cn(
                    "tw:flex tw:flex-col tw:md:grid tw:md:items-start tw:md:grid-rows-[auto]",
                    "tw:md:grid-cols-[repeat(2,minmax(10px,1fr))] tw:md:gap-3.75",
                    "tw:lg:grid-cols-[repeat(3,minmax(10px,1fr))]",
                    "tw:space-y-3.75 tw:md:space-y-0"
                )}
            >
                <div
                    className={cn(
                        "tw:md:row-start-1 tw:md:col-start-1 tw:md:row-end-2 tw:md:col-end-2"
                    )}
                >
                    <ProductPage.ImageViewer />
                </div>
                <div
                    className={cn(
                        "tw:hidden tw:md:block",
                        "tw:md:row-start-2 tw:md:col-start-1 tw:md:row-end-3 tw:md:col-end-2",
                        "tw:lg:row-start-1 tw:lg:col-start-2 tw:lg:row-end-2 tw:lg:col-end-3"
                    )}
                >
                    <ProductPage.ProductFacing
                        className={cn("tw:hidden", "tw:lg:flex")}
                    />

                    <ProductPage.Promotions
                        className={cn("tw:hidden tw:md:block")}
                    />

                    <div
                        className={cn(
                            "tw:flex tw:items-center tw:justify-between tw:flex-wrap"
                        )}
                    >
                        {product.brand && (
                            <div>
                                <ProductPage.BrandLogo />
                            </div>
                        )}
                        <EnergyLabel
                            energyLabel={energyLabel}
                            className={cn("tw:flex tw:w-fit tw:justify-end")}
                        >
                            <EnergyLabel.LabelNested discriminator={"md"} />
                            <EnergyLabel.Fiche />
                        </EnergyLabel>
                    </div>

                    <KeyFeatures
                        className={cn("tw:hidden tw:lg:block")}
                        features={features}
                    />

                    <ProductPage.PromoBanner />
                </div>
                <div
                    className={cn(
                        {
                            "tw:sticky tw:top-6": isSticky
                        },
                        "tw:md:row-start-1 tw:md:col-start-2 tw:md:row-end-[span_5] tw:md:col-end-3",
                        "tw:lg:row-start-1 tw:lg:col-start-3 tw:lg:row-end-[span_5] tw:lg:col-end-4"
                    )}
                >
                    <ProductPage.RepairabilityIndex />

                    <EnergyLabel
                        energyLabel={energyLabel}
                        className={cn(
                            "tw:flex tw:md:hidden tw:w-fit tw:justify-start tw:mb-4"
                        )}
                    >
                        <EnergyLabel.LabelNested discriminator={"xs"} />
                        <EnergyLabel.Fiche />
                    </EnergyLabel>

                    <ProductPage.Promotions
                        className={cn("tw:block tw:md:hidden")}
                    />

                    <ECommerceContainer
                        product={product}
                        infoLink={infoLink || ""}
                        tooltips={tooltips}
                        cashbackPagePath={cashbackPagePath}
                        contextAwareXfPath={contextAwareXfPath}
                        paintConfiguratorPath={paintConfiguratorPath}
                    />
                    <Divider
                        className={cn(
                            "tw:hidden tw:px-4 tw:my-8",
                            "tw:md:block"
                        )}
                    />
                    <Usps className={cn("tw:mb-4")} />
                    <InfoCards className={cn("tw:hidden tw:md:flex")} />

                    <ProductPage.PromoBanner
                        className={cn("tw:block tw:md:hidden")}
                    />
                </div>
                <div
                    className={cn(
                        "tw:md:row-start-3 tw:md:col-start-1 tw:md:row-end-4 tw:md:col-end-2",
                        "tw:lg:row-start-4 tw:lg:col-start-1 tw:lg:row-end-5 tw:lg:col-end-3"
                    )}
                >
                    <ProductPage.ProductBundles />
                    <ProductPage.ProductInfo />
                </div>
                <div
                    className={cn(
                        "tw:md:-mt-3.75",
                        "tw:md:row-start-4 tw:md:col-start-1 tw:md:row-end-5 tw:md:col-end-2",
                        "tw:lg:row-start-2 tw:lg:col-start-1 tw:lg:row-end-3 tw:lg:col-end-3"
                    )}
                >
                    <ProductPage.RelatedProductsSlider
                        productSource={ProductSliderSources.Accessory}
                    />

                    <ProductPage.RelatedProductsSlider
                        productSource={ProductSliderSources.MainProduct}
                    />
                </div>

                {isReviewsEnabled && (
                    <div
                        className={cn(
                            "tw:md:row-start-5 tw:md:col-start-1 tw:md:row-end-6 tw:md:col-end-3",
                            "tw:lg:row-start-5 tw:lg:col-start-1 tw:lg:row-end-6 tw:lg:col-end-4"
                        )}
                    >
                        <ProductPage.Review />
                    </div>
                )}

                <InfoCards
                    className={cn(
                        "tw:flex tw:md:hidden",
                        "tw:md:row-start-2 tw:md:col-start-2 tw:md:row-end-3 tw:md:col-end-3",
                        "tw:lg:row-start-2 tw:lg:col-start-3 tw:lg:row-end-3 tw:lg:col-end-4"
                    )}
                />

                <div
                    className={cn(
                        "tw:md:row-start-6 tw:md:col-start-1 tw:md:row-end-7 tw:md:col-end-2",
                        "tw:lg:row-start-3 tw:lg:col-start-1 tw:lg:row-end-4 tw:lg:col-end-3"
                    )}
                >
                    <ProductPage.RecommendProductsSlider />
                </div>
            </div>

            <div>
                <ProductPage.RecentlyViewedProductSlider />
            </div>
        </>
    );
};
